import { MembershipFigure } from '@models/membership/MembershipAnalyticsStatistics'
import { StatisticDeltaUnit } from '@components/charts/analytics/Statistic'

/**
 * Presentation helpers for the Revenue page. Nothing in here computes a
 * business figure: every number comes from an API field and is only read out
 * (`figureValue`) or formatted (`formatPercent`, `formatDecimal`, ...).
 */

/** The value of a figure, whether the API sent a bare number or `{ value }`. */
const figureValue = (figure?: MembershipFigure): number | null => {
  if (figure === undefined || figure === null) return null
  if (typeof figure === 'number') return figure
  return figure.value ?? null
}

/** The delta the API attached to a figure, in the unit the card renders. */
const figureDelta = (
  figure: MembershipFigure | undefined,
  unit: StatisticDeltaUnit = 'percent'
): number | undefined => {
  if (!figure || typeof figure === 'number') return undefined
  const delta = unit === 'absolute' ? figure.deltaValue : figure.deltaPercent
  return typeof delta === 'number' ? delta : undefined
}

/** French decimal separator, like the rest of the analytics Figma copy. */
const formatDecimal = (value: number, digits = 1): string =>
  new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value)

const formatInteger = (value: number | null | undefined): string | undefined =>
  typeof value === 'number'
    ? new Intl.NumberFormat('fr-FR').format(value)
    : undefined

const formatPercent = (value: number | null | undefined, digits = 1) =>
  typeof value === 'number' ? `${formatDecimal(value, digits)}%` : undefined

/** "+191" / "-12": sign only, the number is the API's. */
const formatSignedInteger = (value: number | null | undefined) =>
  typeof value === 'number'
    ? `${value > 0 ? '+' : ''}${new Intl.NumberFormat('fr-FR').format(value)}`
    : undefined

/** Joins the lines that exist; a null field drops its line. */
const lines = (
  ...parts: (string | undefined | false)[]
): string | undefined => {
  const kept = parts.filter(Boolean) as string[]
  return kept.length ? kept.join('\n') : undefined
}

/** Caption under a money tile the API nulled because the scope spans currencies. */
const MIXED_CURRENCIES_CAPTION = 'Several currencies: pick plans of one program'

export {
  MIXED_CURRENCIES_CAPTION,
  figureDelta,
  figureValue,
  formatDecimal,
  formatInteger,
  formatPercent,
  formatSignedInteger,
  lines,
}
